import { Banner, Button, Heading, Modal } from '@boltenergy-be/design-system'
import { ExtraAddressModalProps } from './types'
import { CUSTOMER_TYPES, CustomerType, PAYMENT_METHODS } from '@boltenergy-be/shared'
import { PortalAccount } from 'lib/partners/types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { EMPTY_ACCOUNTS_BY_CUSTOMER_TYPE } from 'features/registration/components/extra-address-modal/constants'
import { setSelectedExtraAddressAccount, updateAllInputs } from 'store/customer/slice'
import { BillingFieldKeys } from 'store/customer/enums'
import AccountCard from 'features/registration/components/account-card/AccountCard'
import { useTranslation } from 'next-i18next/pages'
import classNames from 'classnames'

const ExtraAddressModal = ({ email, customerType, isOpen, onRequestClose, nextStep }: ExtraAddressModalProps) => {
  // Redux
  const { extraAddress, inputs } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['registration', 'common'])

  // Constants
  const usableAccounts = (email && extraAddress?.accounts && extraAddress?.accounts?.[email]) || null
  const allAccountsByCustomerType =
    (usableAccounts &&
      Object.values(usableAccounts).reduce(
        (prev: Record<CustomerType, PortalAccount[]>, acc) => {
          if (acc.company) {
            prev[CUSTOMER_TYPES.professional].push(acc)
          } else {
            prev[CUSTOMER_TYPES.residential].push(acc)
          }
          return prev
        },
        {
          [CUSTOMER_TYPES.residential]: [],
          [CUSTOMER_TYPES.professional]: []
        }
      )) ||
    EMPTY_ACCOUNTS_BY_CUSTOMER_TYPE
  const inverseCustomerType = customerType === CUSTOMER_TYPES.residential ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
  const availableAccounts = allAccountsByCustomerType[customerType]
  const unavailableAccounts = allAccountsByCustomerType[inverseCustomerType]

  /**
   * Handles select account or start from new account
   * @param {PortalAccount} acc
   */
  const handleContinue = (acc?: PortalAccount) => {
    // Select the account in redux to use in create contract later in the registration flow
    dispatch(setSelectedExtraAddressAccount(acc?.customerNumber || undefined))

    // Prefill billing data if an account was selected
    if (acc) {
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            // TODO: should we also prefill data in personalData? As this step is behind us now.
            billing: {
              ...inputs.billing,
              [BillingFieldKeys.SAME_CORRESPONDENCE_ADDRESS]: false,
              [BillingFieldKeys.CORRESPONDENCE_ADDRESS]: {
                ...acc.correspondenceAddress,
                streetBox: acc.correspondenceAddress?.streetBox || undefined,
                postalCode: acc.correspondenceAddress?.postalCode?.toString(),
                townCode: 0,
                countryCode: 'BE'
              },
              [BillingFieldKeys.PAYMENT_METHOD]: acc.paymentDetails?.directDebit ? PAYMENT_METHODS.domiciliation : PAYMENT_METHODS.transfer,
              [BillingFieldKeys.DELIVERY_MODE]: acc.deliveryMode,
              [BillingFieldKeys.IBAN]: acc.paymentDetails?.iban || ''
            }
          }
        })
      )
    }

    onRequestClose()
    nextStep()
  }

  return (
    <Modal {...{ isOpen, onRequestClose }} title={t('extraAddressModal.title')} titleVariant="h4">
      <Banner type="warning" title={t('extraAddressModal.banner.title')}>
        {t('extraAddressModal.banner.description')}
      </Banner>

      {!!availableAccounts?.length && (
        <ul className="flex-col gap-500">
          {availableAccounts.map((account) => (
            <AccountCard {...{ account }} onSelect={() => handleContinue(account)} key={account.customerNumber} />
          ))}
        </ul>
      )}

      {!!unavailableAccounts?.length && (
        <>
          <Heading as="h2" variant="h5" serif className={classNames('text-subdued', { 'mt-400': !!availableAccounts?.length })}>
            {t('extraAddressModal.notAvailable')}
          </Heading>
          <ul className="flex-col gap-500">
            {unavailableAccounts.map((account) => (
              <AccountCard {...{ account }} onSelect={() => handleContinue(account)} key={account.customerNumber} isDisabled />
            ))}
          </ul>
        </>
      )}

      <Button
        type="button"
        onClick={() => handleContinue()}
        variant="secondary"
        size="large"
        trailingIcon="chevronRight"
        isFullwidth
        className="mt-500"
      >
        {t('extraAddressModal.newAccountAction')}
      </Button>
    </Modal>
  )
}

export default ExtraAddressModal
